import { thunkActionLoading } from '@configs'
import { createSlice } from '@reduxjs/toolkit'
import { IFeatureDetail } from 'src/components/evolve-landing-page-management/feature/FeatureDetailModal'
import {
  ICareer,
  ICareerBannerData,
  IJobPosting,
  ISeoPage,
} from 'src/interfaces/career-landing-management'
import { RootState } from '.'
import {
  createCareerAboutLlcAction,
  createCareerApplicationProcessAction,
  createCareerBannerBottomAction,
  createCareerBannerTopAction,
  createCareerWhyJoinUsAction,
  createJobAction,
  deleteCareerApplicationProcessAction,
  deleteCareerWhyJoinUsAction,
  deleteJobAction,
  getCareerAboutLlcAction,
  getCareerApplicationProcessAction,
  getCareerBannerBottomAction,
  getCareerBannerTopAction,
  getCareerPageSeoAction,
  getCareerWhyJoinUsAction,
  getJobAction,
  updateCareerApplicationProcessAction,
  updateCareerPageSeoAction,
  updateCareerWhyJoinUsAction,
} from '../actions/career-landing-management'

interface ICareerLandingManagement {
  bannerTop: ICareerBannerData | undefined
  bannerBottom: ICareerBannerData | undefined
  aboutLLC: ICareerBannerData | undefined
  whyJoinUs: ICareer[] | undefined
  applicationProcess: ICareer[] | undefined
  jobs: IJobPosting[] | undefined
  selectedFeature: IFeatureDetail | undefined
  careerSeo: ISeoPage | undefined
  loadings: Record<string, boolean | undefined>
}

const initialState: ICareerLandingManagement = {
  bannerTop: undefined,
  bannerBottom: undefined,
  aboutLLC: undefined,
  selectedFeature: undefined,
  whyJoinUs: undefined,
  applicationProcess: undefined,
  jobs: undefined,
  careerSeo: undefined,
  loadings: {},
}

const careerLandingManagement = createSlice({
  name: 'careerLandingManagement',
  initialState,
  reducers: {
    clearSelectedFeature: (state) => {
      state.selectedFeature = undefined
    },
    setSelectedFeature: (state, action) => {
      state.selectedFeature = action.payload
    },
  },
  extraReducers: (builder) => {
    // Banner top Actions
    builder.addCase(getCareerBannerTopAction.pending, (state) => {
      state.loadings[thunkActionLoading.GET_BANNER_TOP_LOADING] = true
    })
    builder.addCase(getCareerBannerTopAction.rejected, (state) => {
      state.loadings[thunkActionLoading.GET_BANNER_TOP_LOADING] = false
    })
    builder.addCase(getCareerBannerTopAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.GET_BANNER_TOP_LOADING] = false
      state.bannerTop = action.payload.data
    })
    builder.addCase(createCareerBannerTopAction.pending, (state) => {
      state.loadings[thunkActionLoading.CREATE_BANNER_TOP_LOADING] = true
    })
    builder.addCase(createCareerBannerTopAction.rejected, (state) => {
      state.loadings[thunkActionLoading.CREATE_BANNER_TOP_LOADING] = false
    })
    builder.addCase(createCareerBannerTopAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.CREATE_BANNER_TOP_LOADING] = false
      state.bannerTop = action.payload.data.commonContent
    })
    //banner bottom
    builder.addCase(getCareerBannerBottomAction.pending, (state) => {
      state.loadings[thunkActionLoading.GET_BANNER_BOTTOM_LOADING] = true
    })
    builder.addCase(getCareerBannerBottomAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.GET_BANNER_BOTTOM_LOADING] = false
    })
    builder.addCase(getCareerBannerBottomAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.GET_BANNER_BOTTOM_LOADING] = false
      state.bannerBottom = action.payload.data
    })

    builder.addCase(createCareerBannerBottomAction.pending, (state) => {
      state.loadings[thunkActionLoading.CREATE_BANNER_BOTTOM_LOADING] = true
    })
    builder.addCase(createCareerBannerBottomAction.rejected, (state) => {
      state.loadings[thunkActionLoading.CREATE_BANNER_BOTTOM_LOADING] = false
    })
    builder.addCase(
      createCareerBannerBottomAction.fulfilled,
      (state, action) => {
        state.loadings[thunkActionLoading.CREATE_BANNER_BOTTOM_LOADING] = false
        state.bannerBottom = action.payload.data.commonContent
      }
    )

    //about llc
    builder.addCase(getCareerAboutLlcAction.pending, (state) => {
      state.loadings[thunkActionLoading.GET_ABOUT_LLC_LOADING] = true
    })
    builder.addCase(getCareerAboutLlcAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.GET_ABOUT_LLC_LOADING] = false
    })
    builder.addCase(getCareerAboutLlcAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.GET_ABOUT_LLC_LOADING] = false
      state.aboutLLC = action.payload.data
    })

    builder.addCase(createCareerAboutLlcAction.pending, (state) => {
      state.loadings[thunkActionLoading.CREATE_ABOUT_LLC_LOADING] = true
    })
    builder.addCase(createCareerAboutLlcAction.rejected, (state) => {
      state.loadings[thunkActionLoading.CREATE_ABOUT_LLC_LOADING] = false
    })
    builder.addCase(createCareerAboutLlcAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.CREATE_ABOUT_LLC_LOADING] = false
      state.aboutLLC = action.payload.data.commonContent
    })

    //why join us
    builder.addCase(getCareerWhyJoinUsAction.pending, (state) => {
      state.loadings[thunkActionLoading.GET_WHY_JOIN_US_LOADING] = true
    })
    builder.addCase(getCareerWhyJoinUsAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.GET_WHY_JOIN_US_LOADING] = false
    })
    builder.addCase(getCareerWhyJoinUsAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.GET_WHY_JOIN_US_LOADING] = false
      state.whyJoinUs = action.payload.data
    })
    builder.addCase(createCareerWhyJoinUsAction.pending, (state) => {
      state.loadings[thunkActionLoading.CREATE_WHY_JOIN_US_LOADING] = true
    })
    builder.addCase(createCareerWhyJoinUsAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.CREATE_WHY_JOIN_US_LOADING] = false
    })
    builder.addCase(createCareerWhyJoinUsAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.CREATE_WHY_JOIN_US_LOADING] = false
    })
    builder.addCase(updateCareerWhyJoinUsAction.pending, (state) => {
      state.loadings[thunkActionLoading.UPDATE_WHY_JOIN_US_LOADING] = true
    })
    builder.addCase(updateCareerWhyJoinUsAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.UPDATE_WHY_JOIN_US_LOADING] = false
    })
    builder.addCase(updateCareerWhyJoinUsAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.UPDATE_WHY_JOIN_US_LOADING] = false
      state.whyJoinUs = action.payload.data
    })
    builder.addCase(deleteCareerWhyJoinUsAction.pending, (state) => {
      state.loadings[thunkActionLoading.DELETE_WHY_JOIN_US_LOADING] = true
    })
    builder.addCase(deleteCareerWhyJoinUsAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.DELETE_WHY_JOIN_US_LOADING] = false
    })
    builder.addCase(deleteCareerWhyJoinUsAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.DELETE_WHY_JOIN_US_LOADING] = false
    })

    //application process
    builder.addCase(getCareerApplicationProcessAction.pending, (state) => {
      state.loadings[thunkActionLoading.GET_APPLICATION_LOADING] = true
    })
    builder.addCase(
      getCareerApplicationProcessAction.rejected,
      (state, action) => {
        state.loadings[thunkActionLoading.GET_APPLICATION_LOADING] = false
      }
    )
    builder.addCase(
      getCareerApplicationProcessAction.fulfilled,
      (state, action) => {
        state.loadings[thunkActionLoading.GET_APPLICATION_LOADING] = false
        state.applicationProcess = action.payload.data
      }
    )
    builder.addCase(createCareerApplicationProcessAction.pending, (state) => {
      state.loadings[thunkActionLoading.CREATE_APPLICATION_LOADING] = true
    })
    builder.addCase(
      createCareerApplicationProcessAction.rejected,
      (state, action) => {
        state.loadings[thunkActionLoading.CREATE_APPLICATION_LOADING] = false
      }
    )
    builder.addCase(
      createCareerApplicationProcessAction.fulfilled,
      (state, action) => {
        state.loadings[thunkActionLoading.CREATE_APPLICATION_LOADING] = false
      }
    )
    builder.addCase(updateCareerApplicationProcessAction.pending, (state) => {
      state.loadings[thunkActionLoading.UPDATE_APPLICATION_LOADING] = true
    })
    builder.addCase(
      updateCareerApplicationProcessAction.rejected,
      (state, action) => {
        state.loadings[thunkActionLoading.UPDATE_APPLICATION_LOADING] = false
      }
    )
    builder.addCase(
      updateCareerApplicationProcessAction.fulfilled,
      (state, action) => {
        state.loadings[thunkActionLoading.UPDATE_APPLICATION_LOADING] = false
        state.applicationProcess = action.payload.data
      }
    )
    builder.addCase(deleteCareerApplicationProcessAction.pending, (state) => {
      state.loadings[thunkActionLoading.DELETE_APPLICATION_LOADING] = true
    })
    builder.addCase(
      deleteCareerApplicationProcessAction.rejected,
      (state, action) => {
        state.loadings[thunkActionLoading.DELETE_APPLICATION_LOADING] = false
      }
    )
    builder.addCase(
      deleteCareerApplicationProcessAction.fulfilled,
      (state, action) => {
        state.loadings[thunkActionLoading.DELETE_APPLICATION_LOADING] = false
      }
    )

    //seo
    builder.addCase(getCareerPageSeoAction.pending, (state) => {
      state.loadings[thunkActionLoading.GET_CAREER_SEO_CONFIG_LOADING] = true
    })
    builder.addCase(getCareerPageSeoAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.GET_CAREER_SEO_CONFIG_LOADING] = false
    })
    builder.addCase(getCareerPageSeoAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.GET_CAREER_SEO_CONFIG_LOADING] = false
      state.careerSeo = action.payload.data
    })
    builder.addCase(updateCareerPageSeoAction.pending, (state) => {
      state.loadings[thunkActionLoading.UPDATE_CAREER_SEO_CONFIG_LOADING] = true
    })
    builder.addCase(updateCareerPageSeoAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.UPDATE_CAREER_SEO_CONFIG_LOADING] =
        false
    })
    builder.addCase(updateCareerPageSeoAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.UPDATE_CAREER_SEO_CONFIG_LOADING] =
        false
      state.careerSeo = action.payload.data.commonContent
    })

    // job
    builder.addCase(createJobAction.pending, (state) => {
      state.loadings[thunkActionLoading.CREATE_JOB] = true
    })
    builder.addCase(createJobAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.CREATE_JOB] = false
    })
    builder.addCase(createJobAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.CREATE_JOB] = false
    })
    builder.addCase(getJobAction.pending, (state) => {
      state.loadings[thunkActionLoading.GET_JOB] = true
    })
    builder.addCase(getJobAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.GET_JOB] = false
    })
    builder.addCase(getJobAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.GET_JOB] = false
      state.jobs = action.payload.data.items
    })
    builder.addCase(deleteJobAction.pending, (state) => {
      state.loadings[thunkActionLoading.DELETE_JOB] = true
    })
    builder.addCase(deleteJobAction.rejected, (state, action) => {
      state.loadings[thunkActionLoading.DELETE_JOB] = false
    })
    builder.addCase(deleteJobAction.fulfilled, (state, action) => {
      state.loadings[thunkActionLoading.DELETE_JOB] = false
    })
  },
})

export const careerLandingActions = {
  ...careerLandingManagement.actions,
}

// Selectors
export const selectEvolveLandingLoading = (state: RootState, name: string) =>
  state.evolveLanding.loadings[`${name}`]

export const selectEvolveBannerTop = (state: RootState) =>
  state.evolveLanding.bannerTop

export const selectEvolveFeatures = (state: RootState) =>
  state.evolveLanding.features

export const selectEvolveSelectedFeature = (state: RootState) =>
  state.evolveLanding.selectedFeature

export const selectEvolveFeatureListLoading = (state: RootState) =>
  state.evolveLanding.loadings[
    thunkActionLoading.GET_EVOLVE_FEATURE_LIST_LOADING
  ]

export const selectEvolveFeatureCreateLoading = (state: RootState) =>
  state.evolveLanding.loadings[thunkActionLoading.CREATE_EVOLVE_FEATURE_LOADING]

export const selectEvolveFeatureUpdateLoading = (state: RootState) =>
  state.evolveLanding.loadings[thunkActionLoading.UPDATE_EVOLVE_FEATURE_LOADING]

export const selectEvolveFeatureDeleteLoading = (state: RootState) =>
  state.evolveLanding.loadings[thunkActionLoading.DELETE_EVOLVE_FEATURE_LOADING]

export const selectEvolveFeatureByIdLoading = (state: RootState) =>
  state.evolveLanding.loadings[
    thunkActionLoading.GET_EVOLVE_FEATURE_BY_ID_LOADING
  ]

// Download selectors
export const selectEvolveDownload = (state: RootState) =>
  state.evolveLanding.download
export const selectEvolveDownloadLoading = (state: RootState) =>
  state.evolveLanding.loadings[thunkActionLoading.GET_EVOLVE_DOWNLOAD_LOADING]
export const selectEvolveDownloadCreateLoading = (state: RootState) =>
  state.evolveLanding.loadings[
    thunkActionLoading.CREATE_EVOLVE_DOWNLOAD_LOADING
  ]

export default careerLandingManagement.reducer
